import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo }        from "react";
import { Download, FileSpreadsheet, Loader2, Calendar, X } from "lucide-react";
import * as XLSX           from "xlsx";
import { api }             from "@/lib/api";
import { cn }              from "@/lib/utils";

export const Route = createFileRoute("/_app/exportar/")({
  component: ExportarPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

function fechaHoy() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, "");
}

function fmt(n: any) {
  return Number(n ?? 0).toFixed(2);
}

function descargarWorkbook(wb: XLSX.WorkBook, nombre: string) {
  const buf  = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function getPedidos(desde?: string, hasta?: string): Promise<any[]> {
  const params = new URLSearchParams({ limit: "9999", page: "1" });
  if (desde) params.set("desde", desde);
  if (hasta) params.set("hasta", hasta);
  const res = await api.get(`/pedidos?${params.toString()}`) as any;
  if (Array.isArray(res))       return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

async function getGastos(desde?: string, hasta?: string): Promise<any[]> {
  const params = new URLSearchParams({ limit: "9999", page: "1" });
  if (desde) params.set("desde", desde);
  if (hasta) params.set("hasta", hasta);
  const res = await api.get(`/gastos?${params.toString()}`) as any;
  if (Array.isArray(res))       return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

function pedidosToSheet(pedidos: any[]) {
  const rows = pedidos.map((p) => {
    const productosStr = Array.isArray(p.detalles) && p.detalles.length > 0
      ? p.detalles.map((d: any) => `${d.cantidad}x ${d.producto_nombre ?? d.producto_id}`).join(", ")
      : "";
    return {
      ID:               p.id,
      Fecha:            p.fecha_pedido   ?? "",
      "Fecha entrega":  p.fecha_entrega  ?? "",
      Cliente:          p.cliente_nombre ?? "",
      Productos:        productosStr,
      Tipo:             p.tipo_pedido    ?? "",
      "Método de pago": p.metodo_pago    ?? "",
      "Estado entrega": p.status         ?? "",
      Pagado:           p.pagado ? "Sí" : "No",
      "Total ($)":      fmt(p.total),
      "Descuento ($)":  fmt(p.descuento),
      Notas:            p.notas          ?? "",
    };
  });
  return XLSX.utils.json_to_sheet(rows.length ? rows : [{ Nota: "Sin registros" }]);
}

function gastosToSheet(gastos: any[]) {
  const rows = gastos.map((g) => ({
    ID:          g.id,
    Fecha:       g.fecha       ?? "",
    "Categoría": g.categoria   ?? "",
    Descripción: g.descripcion ?? "",
    "Monto ($)": fmt(g.monto),
  }));
  return XLSX.utils.json_to_sheet(rows.length ? rows : [{ Nota: "Sin registros" }]);
}

// ─── Filtro de fecha ──────────────────────────────────────────────────────────

type FiltroMode = "todo" | "mes";

interface FiltroFecha {
  mode:  FiltroMode;
  mes:   number; // 1-12
  anio:  number;
}

function useFiltroFecha() {
  const now = new Date();
  const [filtro, setFiltro] = useState<FiltroFecha>({
    mode: "mes",
    mes:  now.getMonth() + 1,
    anio: now.getFullYear(),
  });

  const { desde, hasta, label } = useMemo(() => {
    if (filtro.mode === "todo") {
      return { desde: undefined, hasta: undefined, label: "Todas las fechas" };
    }
    const mm   = String(filtro.mes).padStart(2, "0");
    const last = new Date(filtro.anio, filtro.mes, 0).getDate();
    return {
      desde: `${filtro.anio}-${mm}-01`,
      hasta: `${filtro.anio}-${mm}-${String(last).padStart(2, "0")}`,
      label: `${MESES[filtro.mes - 1]} ${filtro.anio}`,
    };
  }, [filtro]);

  const years = useMemo(() => {
  const y = now.getFullYear();
  return [y, y + 1, y + 2, y + 3];
}, []);
  
  return { filtro, setFiltro, desde, hasta, label, years };
}

// ─── Página ───────────────────────────────────────────────────────────────────

type ExportKey = "pedidos" | "gastos" | "todo";

const selectCls = cn(
  "h-9 rounded-lg border border-input bg-background px-3 text-sm font-medium",
  "focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
);

function ExportarPage() {
  const [loading, setLoading] = useState<ExportKey | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const { filtro, setFiltro, desde, hasta, label, years } = useFiltroFecha();

  async function exportPedidos() {
    setLoading("pedidos"); setError(null);
    try {
      const data = await getPedidos(desde, hasta);
      const wb   = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, pedidosToSheet(data), "Pedidos");
      descargarWorkbook(wb, `coldcrew_pedidos_${fechaHoy()}.xlsx`);
    } catch (e: any) {
      setError(`Error al exportar pedidos: ${e?.message ?? String(e)}`);
    } finally { setLoading(null); }
  }

  async function exportGastos() {
    setLoading("gastos"); setError(null);
    try {
      const data = await getGastos(desde, hasta);
      const wb   = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, gastosToSheet(data), "Gastos");
      descargarWorkbook(wb, `coldcrew_gastos_${fechaHoy()}.xlsx`);
    } catch (e: any) {
      setError(`Error al exportar gastos: ${e?.message ?? String(e)}`);
    } finally { setLoading(null); }
  }

  async function exportTodo() {
    setLoading("todo"); setError(null);
    try {
      const [pedidos, gastos] = await Promise.all([getPedidos(desde, hasta), getGastos(desde, hasta)]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, pedidosToSheet(pedidos), "Pedidos");
      XLSX.utils.book_append_sheet(wb, gastosToSheet(gastos),   "Gastos");
      descargarWorkbook(wb, `coldcrew_completo_${fechaHoy()}.xlsx`);
    } catch (e: any) {
      setError(`Error al exportar: ${e?.message ?? String(e)}`);
    } finally { setLoading(null); }
  }

  const opciones = [
    {
      key:    "pedidos" as ExportKey,
      titulo: "Exportar Pedidos",
      desc:   "Pedidos: cliente, tipo, método de pago, estado de entrega, pagado, total y fechas.",
      color:  "#2F6D46",
      action: exportPedidos,
    },
    {
      key:    "gastos" as ExportKey,
      titulo: "Exportar Gastos",
      desc:   "Gastos: categoría, descripción, monto y fecha.",
      color:  "#B74A2E",
      action: exportGastos,
    },
    {
      key:    "todo" as ExportKey,
      titulo: "Exportar Todo",
      desc:   "Un archivo con dos pestañas — Pedidos y Gastos. Ideal para respaldo completo.",
      color:  "#090909",
      action: exportTodo,
    },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground">Exportaciones</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Descarga los datos de Cold Crew en formato Excel (.xlsx). Los archivos se generan directamente en tu dispositivo.
        </p>
      </div>

      {/* ── Filtro de fecha ── */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Calendar className="size-4 text-muted-foreground" />
          <p className="text-sm font-bold text-foreground">Período a exportar</p>
        </div>

        {/* Botones modo — activo usa el mismo degradado del Hero/Sidebar */}
        <div className="flex rounded-lg border border-border overflow-hidden w-fit">
          {(["mes", "todo"] as FiltroMode[]).map((opt) => (
            <button
              key={opt}
              onClick={() => setFiltro((f) => ({ ...f, mode: opt }))}
              style={filtro.mode === opt ? { background: "#090909" } : undefined}
              className={cn(
                "px-4 py-2 text-sm font-semibold transition-colors",
                filtro.mode === opt
                  ? "text-white"
                  : "bg-card text-foreground hover:bg-muted"
              )}
            >
              {opt === "mes" ? "Por mes" : "Todas las fechas"}
            </button>
          ))}
        </div>

        {/* Selectores mes/año */}
        {filtro.mode === "mes" && (
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={filtro.mes}
              onChange={(e) => setFiltro((f) => ({ ...f, mes: Number(e.target.value) }))}
              className={selectCls}
            >
              {MESES.map((mes, i) => (
                <option key={i} value={i + 1}>{mes}</option>
              ))}
            </select>
            <select
              value={filtro.anio}
              onChange={(e) => setFiltro((f) => ({ ...f, anio: Number(e.target.value) }))}
              className={selectCls}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}

        {/* Label del período activo */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
            {filtro.mode === "todo" ? <X className="size-3" /> : <Calendar className="size-3" />}
            {label}
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive break-all font-medium">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {opciones.map((op) => (
          <div key={op.key} className="card-premium p-5 flex items-center gap-4">
            <div className="shrink-0 size-11 rounded-xl flex items-center justify-center"
              style={{ background: `${op.color}18` }}>
              <FileSpreadsheet className="size-5" style={{ color: op.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-foreground">{op.titulo}</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{op.desc}</p>
              <p className="text-[11px] font-semibold mt-1" style={{ color: op.color }}>
                Período: {label}
              </p>
            </div>
            <button
              onClick={op.action}
              disabled={loading !== null}
              className={cn(
                "shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl",
                "text-sm font-bold text-white transition-all active:scale-[0.98]",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
              style={{ background: op.color }}
            >
              {loading === op.key
                ? <><Loader2 className="size-4 animate-spin" /> Generando…</>
                : <><Download className="size-4" /> Descargar</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

